import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import WireLabelMenu from '../../../components/WireLabelMenu/WireLabelMenu';
import { createWireTitle } from '../../../utils/createWireTitle';
import { computeLabelText, getBundleWireInfos } from '../../../utils/wireLabel/computeLabelText';
import { expandBundle, scaleBundleVisuals } from '../../../utils/wireBundling';
import { incrementUpdateCounter } from '../../../redux/utilsSlice';

// Wire (veya bundle rep line) çift tıklanınca açılan etiket menüsünü yönetir.
// Seçilen tipe göre ilgili wire'ın userData'sından metin üretip mevcut
// createWireTitle algoritmasıyla sahneye etiket ekler.
//
// Etiket konumu: tıklama noktasının wire üzerine projeksiyonu (basit en yakın
// segment projeksiyonu). Etiket grup userData.type = 'wireLabel' olarak işaretlenir
// → otomatik wireTitle'lardan ayrılır.
export default function useWireLabelMenu(scene, camera, renderer) {
  const dispatch = useDispatch();
  const [menu, setMenu] = useState(null); // { x, y, previews, isBundleRep, wireInfos, wireCount, visualWireCount } | null
  const targetRef = useRef(null);
  const hitPointRef = useRef(null);
  const bundleGroupRef = useRef(null);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const dom = renderer.domElement;

    const onDblClick = (e) => {
      const rect = dom.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.params.Line = { threshold: 4 };
      raycaster.setFromCamera(ndc, camera);

      // Tıklanabilir wire/rep-line adayları
      const candidates = [];
      scene.traverse(obj => {
        if (!obj.isLine) return;
        const ud = obj.userData || {};
        if (ud.type === 'wire' || ud.isWire || ud.isBundleRep) {
          candidates.push(obj);
        }
      });
      const hits = raycaster.intersectObjects(candidates, false);
      if (!hits.length) return;

      e.preventDefault();
      e.stopPropagation();

      const hit = hits[0];
      const target = hit.object;
      const hitPoint = { x: hit.point.x, y: hit.point.y, z: 0 };
      targetRef.current = target;
      hitPointRef.current = hitPoint;

      // Önizlemeleri hesapla — disabled state'i belirler
      const previews = {
        numara: computeLabelText(scene, target, 'numara'),
        panoNo: computeLabelText(scene, target, 'panoNo'),
        bundle: computeLabelText(scene, target, 'bundle'),
      };
      const isBundleRep = !!target.userData?.isBundleRep;
      let wireInfos = null;
      let wireCount = 0;
      let visualWireCount = null;
      let allWiresSingleEdge = false;
      if (isBundleRep) {
        let bundleGroup = target.parent;
        while (bundleGroup && bundleGroup.userData?.type !== 'wireBundle') bundleGroup = bundleGroup.parent;
        if (bundleGroup) {
          wireInfos = getBundleWireInfos(scene, bundleGroup);
          wireCount = bundleGroup.userData?.wireCount || bundleGroup.userData?.wireIds?.length || 0;
          visualWireCount = bundleGroup.userData?.visualWireCount ?? wireCount;
          bundleGroupRef.current = bundleGroup;
          // Panel çıkış hatlarının vertex sayısını kontrol et:
          // Truncation sonrası wire nesnelerinin vertex sayısına bak.
          // bwdSteps=0 ve enterIdx=0 (ilk kenar) ise wire'ın panel tarafı stub'ı 2 vertex içerir.
          // Bu wires'ların truncated geometrisi 2 vertex'e eşitse görsel genişlik gösterilebilir.
          const wireIds = bundleGroup.userData.wireIds || [];
          if (wireIds.length > 0) {
            const wireVertexCounts = {};
            scene.traverse(o => {
              if (o.isLine && o.userData?.id && wireIds.includes(o.userData.id) && !o.userData._bundleTransient) {
                wireVertexCounts[o.userData.id] = o.geometry?.attributes?.position?.count ?? 0;
              }
            });
            allWiresSingleEdge = wireIds.every(id => (wireVertexCounts[id] ?? 0) === 2);
          }
        }
      }
      setMenu({ x: e.clientX + 6, y: e.clientY + 6, previews, isBundleRep, allWiresSingleEdge, wireInfos, wireCount, visualWireCount });
    };

    dom.addEventListener('dblclick', onDblClick);
    return () => dom.removeEventListener('dblclick', onDblClick);
  }, [scene, camera, renderer]);

  // Menü dışı tıklama → kapat
  useEffect(() => {
    if (!menu) return;
    const onDocDown = () => setMenu(null);
    // Menüye tıklamayı engellemek için stop'u kendi içinde yapıyoruz.
    setTimeout(() => document.addEventListener('mousedown', onDocDown), 0);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [menu]);

  const onUnbundle = () => {
    const target = targetRef.current;
    setMenu(null);
    if (!scene || !target) return;
    const bundleId = target.userData.bundleId || target.userData.id;
    if (!bundleId) return;

    // Bu bundle'a ait wireLabel etiketlerini sahneden kaldır
    const repId = String(target.userData?.id || target.uuid);
    const toRemove = [];
    scene.traverse(o => {
      if (o.userData?.type === 'wireLabel' && String(o.userData?.forWireId) === repId) {
        toRemove.push(o);
      }
    });
    toRemove.forEach(o => (o.parent || scene).remove(o));

    expandBundle(scene, bundleId);
    dispatch(incrementUpdateCounter());
  };

  const onScaleBundle = (visualCount) => {
    const bg = bundleGroupRef.current;
    if (!bg || !scene) return;
    scaleBundleVisuals(scene, bg, visualCount);
    dispatch(incrementUpdateCounter());
  };

  const onSelect = (type) => {
    const target = targetRef.current;
    const point = hitPointRef.current;
    setMenu(null);
    if (!scene || !target || !point) return;

    const text = computeLabelText(scene, target, type);
    if (!text) return;

    // Tıklama noktasına en yakın wire vertex çiftini bul → etiketin yön referansı
    const pos = target.geometry?.attributes?.position;
    let v0 = null, v1 = null;
    if (pos && pos.count >= 2) {
      let bestD = Infinity, bestI = 0;
      for (let i = 0; i < pos.count - 1; i++) {
        const ax = pos.getX(i), ay = pos.getY(i);
        const bx = pos.getX(i + 1), by = pos.getY(i + 1);
        // segment-point distance
        const dx = bx - ax, dy = by - ay;
        const lenSq = dx * dx + dy * dy || 1;
        let t = ((point.x - ax) * dx + (point.y - ay) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
        const px = ax + dx * t, py = ay + dy * t;
        const d = Math.hypot(point.x - px, point.y - py);
        if (d < bestD) { bestD = d; bestI = i; }
      }
      v0 = new THREE.Vector3(pos.getX(bestI), pos.getY(bestI), 0);
      v1 = new THREE.Vector3(pos.getX(bestI + 1), pos.getY(bestI + 1), 0);
    }
    const vertices = v0 && v1 ? [v0, v1] : [];

    const color = (target.material?.color && typeof target.material.color.getStyle === 'function')
      ? target.material.color.getStyle()
      : '#00ffff';

    const basePos = new THREE.Vector3(point.x, point.y, 0);
    const wireId = target.userData?.id || target.uuid;
    const labelGroup = createWireTitle(basePos, text, color, vertices, wireId);
    // Otomatik wireTitle ile karışmasın diye ayrı tip ata
    labelGroup.userData = {
      ...(labelGroup.userData || {}),
      type: 'wireLabel',
      labelType: type,
      forWireId: wireId,
      labelText: text,
    };
    scene.add(labelGroup);
  };

  if (!menu) return null;
  return createPortal(
    <WireLabelMenu
      x={menu.x}
      y={menu.y}
      previews={menu.previews}
      isBundleRep={menu.isBundleRep}
      allWiresSingleEdge={menu.allWiresSingleEdge}
      wireInfos={menu.wireInfos}
      wireCount={menu.wireCount}
      initialVisualWireCount={menu.visualWireCount}
      onSelect={onSelect}
      onUnbundle={onUnbundle}
      onScaleBundle={onScaleBundle}
      onClose={() => setMenu(null)}
    />,
    document.body,
  );
}
